import { useCallback, useMemo } from 'react';

import { getProjectTypeCategory, isContinuousOptimizationType } from 'src/data/project';
import { getCustomerPlan, getCustomerTier } from 'src/data/subscription';
import * as events from 'src/lib/events';
import * as gtm from 'src/lib/gtm';
import { PAGES, useTrackingEvent } from 'src/services/useTrackingEvent';
import { useCustomerActions, useProjectActions, useUiActions } from 'src/store';

export const useCommonEvents = () => {
  const customerActions = useCustomerActions();
  const projectActions = useProjectActions();
  const trackingEvent = useTrackingEvent();
  const uiActions = useUiActions();

  const trackEnteredProject = useCallback(
    (project) => {
      const customer = customerActions.getCurrentCustomer();

      trackingEvent(events.PROJECT.enteredProject, {
        projectId: project.id,
        projectType: project.type,
        projectCategory: getProjectTypeCategory(project.type),
        projectName: project.name,
        currentPlan: getCustomerPlan(customer),
        currentTier: getCustomerTier(customer),
        page: PAGES.homePage,
      });
    },
    [customerActions, trackingEvent]
  );

  const trackExplainerVideoClick = useCallback(() => {
    const customer = customerActions.getCurrentCustomer();
    const project = projectActions.getCurrentProject();

    trackingEvent(events.PROJECT.clickedExplainerVideo, {
      projectId: String(project.id),
      projectCategory: getProjectTypeCategory(project.type),
      projectType: project.type,
      currentPlan: getCustomerPlan(customer),
      currentTier: getCustomerTier(customer),
    });
  }, [customerActions, projectActions, trackingEvent]);

  const trackProjectCreation = useCallback(
    (project) => {
      if (isContinuousOptimizationType(project.type)) {
        trackingEvent(events.WTM.projectCreated, {
          projectId: project.id,
          project: PAGES.wtm,
        });
      }
      if (uiActions.wasWelcomePopupShown()) {
        gtm.pushDataLayerEvent(gtm.EVENTS.firstQuery);
      }

      gtm.pushDataLayerEvent(gtm.EVENTS.projectCreated);
    },
    [uiActions, trackingEvent]
  );

  return useMemo(
    () => ({
      trackEnteredProject,
      trackExplainerVideoClick,
      trackProjectCreation,
    }),
    [trackEnteredProject, trackExplainerVideoClick, trackProjectCreation]
  );
};
